export type ParticleShape = 'sphere' | 'solidSphere' | 'disk' | 'ring' | 'doubleSphere';
export type MassShape = 'single' | 'double' | 'symmetricDouble' | 'quad' | 'cube' | 'star';
export type VelocityMode = 'uniform' | 'keplerian' | 'tidalFlyby' | 'cloudCollision' | 'binaryLagrange' | 'rotatingCollapse';

export interface Preset {
  name: string;
  gravity: number;
  displacement: number;
  colourScale: number;
  particleShape: ParticleShape;
  radius: number;
  offsetX: number;
  offsetY: number;
  velocityX: number;
  velocityY: number;
  massShape: MassShape;
  velocityMode: VelocityMode;
  legacyMotionTuned: boolean;
}

const p = (name: string, gravity: number, displacement: number, colourScale: number, particleShape: ParticleShape, radius: number, offsetX: number, offsetY: number, velocityX: number, velocityY: number, massShape: MassShape, velocityMode: VelocityMode = 'uniform'): Preset =>
  ({ name, gravity, displacement, colourScale, particleShape, radius, offsetX, offsetY, velocityX, velocityY, massShape, velocityMode, legacyMotionTuned: false });

// Values are preserved from legacy index.html. Friendly names are new UI-only labels.
const CURRENT_PRESETS: Preset[] = [
  p('Crystal Core', 3600, 206, 3, 'sphere', 10, 0, 0, 39.3, 0, 'cube'),
  p('Binary Disk', 4300, 153, 25, 'disk', 602, 0, 0, 3, 0, 'double'),
  p('Rapid Binary', 50000, 153, 25, 'disk', 602, 0, 0, 11.2, 0, 'double'),
  p('Quiet Lattice', 500, 208, 5, 'sphere', 331, 0, 0, 0, 0, 'cube'),
  p('Twin Spark', 5800, 186, 25, 'sphere', 10, 0, 0, 34.1, 0, 'double'),
  p('Soft Orbit', 500, 208, 3, 'disk', 200, 0, 0, 1.1, 0, 'double'),
  p('Blue Binary', 1400, 142, 25, 'sphere', 10, 0, 0, 20, 0, 'double'),
  p('Six Point Star', 500, 208, 5, 'sphere', 331, 0, 0, 0, 0, 'star'),
  p('Offset Ring', 2600, 363, 25, 'disk', 602, 878, 0, 1.1, 0, 'double'),
  p('Fast Wanderer', 19000, 671, 25, 'disk', 266, 878, 0, 4.5, 0, 'double'),
  p('Deep Encounter', 32800, 517, 25, 'disk', 451, 870, 0, 5.9, 0, 'double'),
  p('Close Passage', 50000, 517, 25, 'disk', 506, 870, 0, 7.4, 0, 'double'),
  p('Radiant Cube', 40500, 505, 7, 'sphere', 10, 0, 0, 73.2, 0, 'cube'),
  p('Grand Arc', 35400, 517, 7, 'disk', 1500, 610, 0, 4.9, 0, 'double'),
  p('Dense Cluster', 5400, 152, 5, 'solidSphere', 50, 0, 0, 11.6, 0, 'cube'),
  p('Keplerian Galaxy', 5200, 0, 18, 'disk', 650, 0, 0, 0, 0, 'single', 'keplerian'),
  p('Tidal Disruption', 32000, 0, 22, 'solidSphere', 55, 750, 0, 6.2, 0, 'single', 'tidalFlyby'),
  p('Two-Cloud Collision', 1800, 0, 12, 'doubleSphere', 110, 0, 0, 4.2, 0, 'single', 'cloudCollision'),
  p('Binary Lagrange Lace', 7000, 160, 18, 'ring', 650, 0, 0, 0, 0, 'symmetricDouble', 'binaryLagrange'),
  p('Rotating Collapse', 6500, 0, 14, 'solidSphere', 450, 0, 0, 0, 0, 'single', 'rotatingCollapse')
];

export const PRESETS: Preset[] = CURRENT_PRESETS.flatMap((preset) => [
  preset,
  {
    ...preset,
    name: `${preset.name} — Tuned`,
    legacyMotionTuned: true
  }
]);

export function hasVariableVelocity(preset: Preset): boolean {
  return preset.velocityMode !== 'uniform' || preset.legacyMotionTuned;
}

export const QUALITY = {
  performance: { size: 256, label: 'Performance', scale: 0.68, simulationHz: 36 },
  balanced: { size: 384, label: 'Balanced', scale: 0.76, simulationHz: 45 },
  quality: { size: 512, label: 'Quality', scale: 0.82, simulationHz: 60 }
} as const;

export type QualityName = keyof typeof QUALITY;
